import { useId, useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  LoaderCircle,
  RotateCw,
  Trash2,
} from 'lucide-react';
import type { EmailStatusFeedRow } from '@/services/email.service';
import { emailsCardModel, type EmailsCardRow } from '@/lib/emailsCardModel';

export interface EmailsReadingCardProps {
  emails: readonly EmailStatusFeedRow[];
  /** Epoch ms — drives the Still reading wording. */
  now: number;
  /** First sync: open by default (AC6); a later false→true also opens it. */
  defaultOpen?: boolean;
  /** Emails whose Try again is in flight; their button is disabled. */
  retryingIds: ReadonlySet<string>;
  onRetry(emailId: string): void;
  onDiscard(emailId: string): void;
}

const ICON_BUTTON_CLASS = [
  // 32px as drawn; the `before:` box extends the tap area to 44px.
  'relative inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground',
  'before:absolute before:-inset-1.5',
  'hover:bg-surface-muted disabled:cursor-default disabled:opacity-50',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
].join(' ');

function Row({
  row,
  retrying,
  onRetry,
  onDiscard,
}: {
  row: EmailsCardRow;
  retrying: boolean;
  onRetry(emailId: string): void;
  onDiscard(emailId: string): void;
}) {
  const failed = row.state === 'failed';
  return (
    <li
      className={[
        'flex items-center gap-3 rounded-md px-3 py-2',
        failed ? 'bg-danger-light' : 'bg-surface',
      ].join(' ')}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        {row.sender && (
          <p className="m-0 truncate text-sm font-medium text-foreground">
            {row.sender}
          </p>
        )}
        <p className="m-0 min-w-0 truncate text-sm font-normal text-muted-foreground">
          {row.subject}
        </p>
      </div>
      {failed ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            aria-label="Try again"
            disabled={retrying}
            onClick={() => onRetry(row.id)}
            className={ICON_BUTTON_CLASS}
          >
            <RotateCw
              size={18}
              strokeWidth={1.5}
              aria-hidden="true"
              className={retrying ? 'motion-safe:animate-spin' : undefined}
            />
          </button>
          <button
            type="button"
            aria-label="Discard"
            onClick={() => onDiscard(row.id)}
            className={ICON_BUTTON_CLASS}
          >
            <Trash2
              size={18}
              strokeWidth={1.5}
              aria-hidden="true"
            />
          </button>
        </div>
      ) : (
        <LoaderCircle
          size={18}
          strokeWidth={1.5}
          aria-label="Reading"
          role="img"
          className="shrink-0 text-icon-default motion-safe:animate-spin"
        />
      )}
    </li>
  );
}

/**
 * `.email-status` (Review Flow v2.0, Figma 11498-40822) — the Review screen's
 * card for emails Yaruta is still reading or couldn't read (YAR-553).
 *
 * A collapsible card: the header carries the two count lines (failed in
 * error red, Still reading in warning amber, plain Reading as a caption) and
 * a chevron; it is closed by default and open on first sync. Open, each
 * email shows its sender over its subject; a failed row is tinted and offers
 * Try again and Discard, a reading row shows a spinner.
 *
 * Presentational: what it says comes from `emailsCardModel`; retrying,
 * discarding and the feed itself belong to the page (C4).
 */
export function EmailsReadingCard({
  emails,
  now,
  defaultOpen = false,
  retryingIds,
  onRetry,
  onDiscard,
}: EmailsReadingCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  // `defaultOpen` turning on later (first sync detected after mount) opens
  // the card; turning off never closes one the parent opened.
  const [prevDefaultOpen, setPrevDefaultOpen] = useState(defaultOpen);
  if (defaultOpen !== prevDefaultOpen) {
    setPrevDefaultOpen(defaultOpen);
    if (defaultOpen) setOpen(true);
  }
  const listId = useId();
  const model = emailsCardModel(emails, now);
  if (!model.visible) return null;

  const Chevron = open ? ChevronUp : ChevronDown;

  return (
    <section className="flex w-full flex-col rounded-md border border-border bg-surface">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-3 rounded-md p-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <span className="flex min-w-0 flex-1 flex-col">
          {model.failedLine && (
            <span className="truncate text-sm font-medium text-text-error">
              {model.failedLine}
            </span>
          )}
          {model.readingLine && (
            <span
              className={[
                'truncate text-sm',
                model.stillReading
                  ? 'font-medium text-warning'
                  : 'font-normal text-text-caption',
              ].join(' ')}
            >
              {model.readingLine}
            </span>
          )}
        </span>
        <Chevron
          size={20}
          strokeWidth={1.5}
          aria-hidden="true"
          className="shrink-0 text-icon-default"
        />
      </button>

      {open && (
        <ul
          id={listId}
          className="m-0 flex list-none flex-col gap-1 px-2 pb-2"
        >
          {model.rows.map((row) => (
            <Row
              key={row.id}
              row={row}
              retrying={retryingIds.has(row.id)}
              onRetry={onRetry}
              onDiscard={onDiscard}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
